import { makeAutoObservable } from "mobx";

// Стоимость новой игры
export const gamePrice = 100;

// Число попыток отгадать слово по умолчанию
export const maxBasicAttempts = 5;

// Максимальное число попыток отгадать слово
export const maxAttempts = 6;

// Максимальное число букв в слове
export const maxLetters = 10;

// Базовый лимит времени игры, чтобы таймер не мигал нулём при старте.
export const DEFAULT_GAME_TIME_SECONDS = 60;

// Набор букв по умолчанию для клавиатуры
export const defaultLetters = "etaoinshrd".split("");
// Приводит строку букв к массиву с запасным вариантом
export const normalizeLetters = (letters) => {
    if (typeof letters === "string" && letters.length > 0) {
        return letters.split("");
    }
    return defaultLetters;
};

// Максимальное число игр в день одним игроком
export const maxBasicDaileAttempts = 1;

// Настройка клавиатуры
const basicLetterInfo = {
    type: "letter", value: "", isUsed: false, isExists: false, isExactlyExists: false,
};
const row = Array(maxLetters).fill(basicLetterInfo);
const defaultBoardTemplate = Array(maxBasicAttempts).fill(row);

class Game {
    // Флаг, активна ли игра
    isActive = true;

    // Номер попытки отгадать слово
    attemptNumber = 0;

    // Состояние конкретной попытки пользователя
    attempt = [];

    // История попыток пользователя
    attemptHistory = Array(maxBasicAttempts).fill(null);

    // Игровое поле
    board = defaultBoardTemplate;

    // Текущий счет
    score = 0;

    // Лучший счет игрока за все время
    bestScore = 0;

    // Счетчик комбо
    comboCount = 0;

    // Метка времени последнего валидного слова для отсчёта серии комбо
    lastComboTimestamp = null;

    // Найденные слова
    foundWords = [];

    // Остаток времени в секундах
    timeLeft = DEFAULT_GAME_TIME_SECONDS;

    // Базовый лимит времени
    timeLimitSeconds = DEFAULT_GAME_TIME_SECONDS;

    // Баланс подсказок по типам
    hintWallet = {};

    // Лимиты подсказок
    hintLimits = {};

    // Текущий набор букв
    letters = "";

    // Количество игр в день
    dailyAttempts = maxBasicDaileAttempts;

    // Баланс подсказок игрока (кошелек уровня игрока)
    playerHintBalances = {};
    // Флаг загрузки балансов подсказок для предотвращения мигания UI
    isPlayerHintBalancesLoaded = false;

    // Количество заработанных монет по итогам игры
    earnedCoins = 0;

    // Идентификатор игры
    gameID = "";

    // Загаданное слово
    word = "";

    constructor() {
        makeAutoObservable(this)
    }

    setIsActive(value) {
        this.isActive = value;
    }

    incrementAttemptNumber() {
        this.attemptNumber += 1;
    }

    addLetterToAttempt(value, keyIndex = null) {
        const data = {
            value: value, isUsed: false, isExists: false, isExactlyExists: false,
        };

        if (Number.isFinite(keyIndex)) {
            data.keyIndex = keyIndex;
        }

        this.attempt.push(data);
        this.attemptHistory[this.attemptNumber] = this.attempt;
    }

    deleteLastLetterFromAttempt() {
        this.attempt.pop();
        this.attemptHistory[this.attemptNumber] = this.attempt;
    }

    flushAttempt() {
        this.attempt = [];
    }

    addAttemptToHistory(value) {
        this.attemptHistory[this.attemptNumber] = value;
    }

    addBonusAttempt() {
        this.attemptHistory.push(null);
        this.board.push(row);
    }

    setScore(value) {
        this.score = value;
    }

    setBestScore(value) {
        this.bestScore = value;
    }

    setComboCount(value) {
        this.comboCount = value;
    }

    // Обновляет отметку времени последнего слова для внешней логики комбо
    setLastComboTimestamp(value) {
        this.lastComboTimestamp = value;
    }

    // Сбрасывает значение комбо и связанные с ним отметки времени
    resetCombo() {
        this.comboCount = 0;
        this.lastComboTimestamp = null;
    }

    setFoundWords(words) {
        this.foundWords = Array.isArray(words) ? words : [];
    }

    addFoundWord(word) {
        if (!word || this.foundWords.includes(word)) {
            return;
        }

        this.foundWords = [...this.foundWords, word];
    }

    setTimeLeft(value) {
        this.timeLeft = value;
    }

    setTimeLimitSeconds(value) {
        this.timeLimitSeconds = value;
    }

    setHintWallet(wallet) {
        this.hintWallet = wallet || {};
    }

    setHintLimits(limits) {
        this.hintLimits = limits || {};
    }

    setLetters(value) {
        this.letters = value || "";
    }

    setDailyAttempts(value) {
        this.dailyAttempts = value;
    }

    setEarnedCoins(value) {
        this.earnedCoins = value;
    }

    setPlayerHintBalances(value) {
        this.playerHintBalances = value || {};
        this.isPlayerHintBalancesLoaded = true;
    }

    setGameID(value) {
        this.gameID = value;
    }

    setWord(value) {
        this.word = value;
    }

    setGame(game) {
        this.flush();
        this.setGameID(game.id);

        if (game.hints_json && game.hints_json.length > 0) {
            const hints = JSON.parse(game.hints_json);
            if (hints.includes("attempt")) {
                this.addBonusAttempt();
            }
        }

        if (game.attempts_json && game.attempts_json.length > 0) {
            const attempts = JSON.parse(game.attempts_json);
            attempts.forEach((value) => {
                this.addAttemptToHistory(value);
                this.incrementAttemptNumber();
            });
        }
    }

    // Восстанавливает историю попыток по списку найденных слов.
    restoreAttemptsFromFoundWords(words) {
        if (!Array.isArray(words) || words.length === 0) {
            return;
        }

        const attempts = words.map((word) => String(word).split("").map((value) => ({value})));
        this.attemptHistory = attempts;
        this.attemptNumber = attempts.length;
    }

    flush() {
        this.isActive = true;
        this.attemptNumber = 0;
        this.attempt = [];
        this.attemptHistory = Array(maxBasicAttempts).fill(null);
        this.board = defaultBoardTemplate;
        this.score = 0;
        this.comboCount = 0;
        this.foundWords = [];
        this.timeLeft = DEFAULT_GAME_TIME_SECONDS;
        this.timeLimitSeconds = DEFAULT_GAME_TIME_SECONDS;
        this.hintWallet = {};
        this.hintLimits = {};
        this.letters = "";
        this.gameID = "";
        this.word = "";
        this.playerHintBalances = {};
        this.isPlayerHintBalancesLoaded = false;
        this.lastComboTimestamp = null;
    }
}

const GameStore = new Game();
export default GameStore;
